import store from '@/store';
import availablePaymentEngines from '../constants/PaymentEngine';

/**
 * Checks if Stripe should be used as the payment engine.
 * @param {string} paymentEngine - The current payment engine identifier
 * @returns {boolean} True if Stripe is enabled and matches the payment engine
 */
export function shouldUseStripe(paymentEngine) {
  const stripeConfig = store.getState()?.whitelabelConfig?.env?.stripe;
  return Boolean(stripeConfig?.enabled) && paymentEngine === availablePaymentEngines.stripe;
}

/**
 * Check if Stripe Google Pay is enabled in whitelabel configuration.
 * Expected tenant config: `env.stripe.googlePay = { enabled: boolean }`. Unlike Adyen,
 * `enabled` is the only required param — the Stripe Express Checkout Element does not need
 * `merchantName`/`merchantId`/`buttonType`/`buttonColor`.
 * @returns {boolean} True if Stripe Google Pay is enabled
 */
export function isStripeGooglePayEnabled() {
  const stripeConfig = store.getState()?.whitelabelConfig?.env?.stripe;
  return Boolean(stripeConfig?.enabled && stripeConfig?.googlePay?.enabled);
}

/**
 * Get Stripe Google Pay configuration from whitelabel config.
 * @returns {Object|null} Stripe Google Pay configuration or null if not available
 */
export function getStripeGooglePayConfig() {
  if (!isStripeGooglePayEnabled()) {
    return null;
  }

  return store.getState().whitelabelConfig.env.stripe.googlePay;
}

/**
 * Check if Stripe Apple Pay is enabled in whitelabel configuration.
 * Expected tenant config: `env.stripe.applePay = { enabled: boolean }`. Like Stripe Google Pay
 * and unlike Adyen, `enabled` is the only required param — the Stripe Express Checkout Element
 * does not need `merchantName`/`merchantId`/`buttonType`/`buttonColor` (Stripe manages the Apple
 * merchant ID).
 * @returns {boolean} True if Stripe Apple Pay is enabled
 */
export function isStripeApplePayEnabled() {
  const stripeConfig = store.getState()?.whitelabelConfig?.env?.stripe;
  return Boolean(stripeConfig?.enabled && stripeConfig?.applePay?.enabled);
}

/**
 * Get Stripe Apple Pay configuration from whitelabel config.
 * @returns {Object|null} Stripe Apple Pay configuration or null if not available
 */
export function getStripeApplePayConfig() {
  if (!isStripeApplePayEnabled()) {
    return null;
  }

  return store.getState().whitelabelConfig.env.stripe.applePay;
}
